import React, { useState, useRef, useMemo, createContext, useContext, ReactNode } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
} from '@floating-ui/react'
import type { Placement, UseHoverProps } from '@floating-ui/react'
import { ArrowWrapper, TooltipText, TooltipTriggerContainer, TooltipWrapper } from './style'

export interface TooltipProps {
  initialOpen?: boolean
  placement?: Placement
  open?: boolean
  onOpenChange?: (open: boolean) => void
  title?: ReactNode
  description?: ReactNode
  hasArrow?: boolean
  offsetValue?: number
  maxWidth?: number
  $textAlign?: React.CSSProperties['textAlign']
  wordBreak?: React.CSSProperties['wordBreak']
  $whiteSpace?: React.CSSProperties['whiteSpace']
  className?: string
  showOnFocus?: boolean
  content?: React.ReactNode
  noPortal?: boolean
  noPadding?: boolean
  opacity?: number
  delay?: UseHoverProps['delay']
  'data-is-sensitive'?: boolean
}

const ARROW_SIZE = 16

const useTooltip = ({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  offsetValue,
  showOnFocus,
  delay,
}: TooltipProps = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen

  const arrowRef = useRef(null)

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetValue),
      flip({
        fallbackAxisSideDirection: 'start',
        crossAxis: placement.includes('-'),
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef, padding: 0 }),
    ],
  })

  const context = data.context

  const hover = useHover(context, { move: false, enabled: controlledOpen == null, delay })
  const focus = useFocus(context, { enabled: showOnFocus ?? controlledOpen == null })

  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const interactions = useInteractions([hover, focus, dismiss, role])

  return useMemo(() => ({ open, setOpen, arrowRef, ...interactions, ...data }), [open, setOpen, interactions, data])
}

type ContextType = ReturnType<typeof useTooltip> | null

const TooltipContext = createContext<ContextType>(null)

const useTooltipState = () => {
  const context = useContext(TooltipContext)

  if (context == null) throw new Error('Tooltip components must be wrapped in <Tooltip />')

  return context
}

const Tooltip = ({
  children,
  title,
  description,
  hasArrow,
  className,
  maxWidth,
  $textAlign = 'left',
  wordBreak,
  $whiteSpace = 'pre-wrap',
  content,
  noPortal,
  'data-is-sensitive': dataIsSensitive,
  offsetValue = 0,
  noPadding,
  opacity,
  delay,
  ...options
}: { children: React.ReactNode } & TooltipProps) => {
  const tooltip = useTooltip({ ...options, offsetValue: hasArrow ? ARROW_SIZE + offsetValue : offsetValue, delay })

  const { arrow } = tooltip.middlewareData
  const arrowEl = tooltip?.arrowRef?.current

  const shortPlacement = tooltip.placement.split('-')[0] as 'top' | 'right' | 'bottom' | 'left'
  const staticSide = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' }[shortPlacement]

  if (arrowEl)
    Object.assign(tooltip.arrowRef.current.style, {
      left: arrow?.x != null ? `${arrow.x}px` : '',
      top: arrow?.y != null ? `${arrow.y}px` : '',
      right: '',
      bottom: '',
      [staticSide]: `${-ARROW_SIZE}px`,
    })

  return (
    <TooltipContext.Provider value={tooltip}>
      {children}
      {(title || description || content) && (
        <TooltipContent
          noPortal={noPortal}
          className={className}
          maxWidth={maxWidth}
          textAlign={$textAlign}
          wordBreak={wordBreak}
          whiteSpace={$whiteSpace}
          opacity={opacity}
          noPadding={noPadding}
        >
          <TooltipText data-is-sensitive={dataIsSensitive}>
            {!!title && <h1>{title}</h1>}
            {!!description && <p>{description}</p>}
            {content}
          </TooltipText>
          {hasArrow && <TooltipArrow />}
        </TooltipContent>
      )}
    </TooltipContext.Provider>
  )
}

export const TooltipTrigger = React.forwardRef<HTMLElement, React.HTMLProps<HTMLElement> & { asChild?: boolean }>(
  function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
    const state = useTooltipState()

    const ref = useMergeRefs([state.refs.setReference, propRef])

    if (asChild && React.isValidElement(children))
      return React.cloneElement(
        children,
        state.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          'data-state': state.open ? 'open' : 'closed',
        }),
      )

    return (
      <TooltipTriggerContainer
        ref={state.refs.setReference}
        data-state={state.open ? 'open' : 'closed'}
        {...state.getReferenceProps(props)}
        className="tooltip-trigger"
      >
        {children}
      </TooltipTriggerContainer>
    )
  },
)

interface TooltipContentProps extends React.HTMLProps<HTMLDivElement> {
  maxWidth?: number
  textAlign?: TooltipProps['$textAlign']
  wordBreak?: TooltipProps['wordBreak']
  whiteSpace?: TooltipProps['$whiteSpace']
  noPortal?: boolean
  opacity?: number
  noPadding?: boolean
}

export const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(function TooltipContent(
  { opacity, noPadding, maxWidth, ...props },
  propRef,
) {
  const state = useTooltipState()

  const ref = useMergeRefs([state.refs.setFloating, propRef])

  if (!state.open) return null

  const wrapper = (
    <TooltipWrapper
      ref={ref}
      $noPadding={noPadding}
      $opacity={opacity}
      style={{
        position: state.strategy,
        top: state.y ?? 0,
        left: state.x ?? 0,
        visibility: state.x == null ? 'hidden' : 'visible',
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        ...props.style,
      }}
      {...state.getFloatingProps(props)}
    />
  )

  if (props.noPortal) return wrapper
  return <FloatingPortal>{wrapper}</FloatingPortal>
})

export function TooltipArrow() {
  const state = useTooltipState()
  const { offset } = state.context.middlewareData

  const rotate: Partial<Record<Placement, number>> = { top: 0, right: 90, bottom: 180, left: 270 }
  const placement = (offset?.placement ? offset.placement.split('-')[0] : 'top') as Placement
  const rotateDegrees = rotate[placement]

  return (
    <ArrowWrapper
      ref={state.arrowRef}
      style={{ height: `${ARROW_SIZE}px`, width: `${ARROW_SIZE}px`, transform: `rotate(${rotateDegrees}deg)` }}
    />
  )
}

export default Tooltip

export const TooltipWithTrigger: React.FC<React.PropsWithChildren<TooltipProps>> = ({ children, ...tooltipProps }) => {
  return (
    <Tooltip {...tooltipProps}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
    </Tooltip>
  )
}
